import React, { useState } from 'react';
import BackButtonBottom from '../../components/common/BackButtonBottom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getDriverTaxSummary, downloadFromSignedUrl } from '../../services/complianceService';
import toast from 'react-hot-toast';

// Format period label for display
function formatPeriod(period) {
  if (/^\d{4}-Q[1-4]$/.test(period)) {
    const [y, q] = period.split('-Q');
    return `${y} Q${q}`;
  }
  if (/^\d{4}-\d{2}$/.test(period)) {
    const [y, m] = period.split('-').map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
  }
  if (/^\d{4}$/.test(period)) return `Full year ${period}`;
  return period;
}

// Generate sensible period options (last 3 years × monthly + quarterly + annual)
function periodOptions() {
  const out = [];
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  // Current year monthly (back to start of year)
  for (let m = currentMonth; m >= 0; m--) {
    const period = `${currentYear}-${String(m + 1).padStart(2, '0')}`;
    out.push({ period, group: 'Months (this year)', label: formatPeriod(period) });
  }
  // Previous year monthly (only 4 most recent months back from year-end)
  const prevYear = currentYear - 1;
  for (let m = 11; m >= 8; m--) {
    const period = `${prevYear}-${String(m + 1).padStart(2, '0')}`;
    out.push({ period, group: 'Months (last year)', label: formatPeriod(period) });
  }
  // Quarters — current year up to current quarter, plus all previous year
  const currentQ = Math.floor(currentMonth / 3) + 1;
  for (let q = currentQ; q >= 1; q--) {
    out.push({ period: `${currentYear}-Q${q}`, group: 'Quarters', label: `${currentYear} Q${q}` });
  }
  for (let q = 4; q >= 1; q--) {
    out.push({ period: `${prevYear}-Q${q}`, group: 'Quarters', label: `${prevYear} Q${q}` });
  }
  // Annual
  out.push({ period: String(currentYear), group: 'Full year', label: `Full year ${currentYear}` });
  out.push({ period: String(prevYear), group: 'Full year', label: `Full year ${prevYear}` });

  return out;
}

export default function DriverTaxSummary() {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);

  const handleGenerate = async () => {
    if (!selectedPeriod) {
      toast.error('Pick a period');
      return;
    }
    setGenerating(true);
    setResult(null);
    try {
      const res = await getDriverTaxSummary(selectedPeriod);
      setResult(res);
      if (res.tripCount === 0) {
        toast('No completed trips in this period.', { icon: 'ℹ️' });
      } else {
        toast.success(`${res.tripCount} trip${res.tripCount !== 1 ? 's' : ''} summarised. Net earnings: $${res.summary.totalNetEarnings.toFixed(2)}`);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to generate summary');
    }
    setGenerating(false);
  };

  // Group options for nicer dropdown
  const grouped = periodOptions().reduce((acc, opt) => {
    if (!acc[opt.group]) acc[opt.group] = [];
    acc[opt.group].push(opt);
    return acc;
  }, {});

  return (
    <div className="max-w-[700px] mx-auto p-6">
      <div className="mb-4">
        <button onClick={() => navigate('/driver')} className="bg-transparent border-0 text-brand-primary font-semibold text-[0.9rem] cursor-pointer p-0">← Dashboard</button>
      </div>

      <h1 className="font-serif mt-0 mb-2">Tax summaries</h1>
      <p className="text-body text-[0.9rem] mb-6">
        Generate a tax summary of your Reservo trips for a specific period. Use this as a starting point for your BAS.
        You are responsible for verifying figures against your own records and for your tax compliance.
      </p>

      <div className="p-[18px] rounded-2xl bg-white border border-slate-200 mb-4">
        <div className="form-group">
          <label className="form-label">Reporting period *</label>
          <select className="form-input form-select" value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}>
            <option value="">Pick a period...</option>
            {Object.entries(grouped).map(([group, opts]) => (
              <optgroup key={group} label={group}>
                {opts.map(o => <option key={o.period} value={o.period}>{o.label}</option>)}
              </optgroup>
            ))}
          </select>
          <div className="text-[0.78rem] text-muted mt-1">
            Monthly periods are useful for cash-flow tracking. Quarterly aligns with BAS lodgement.
          </div>
        </div>

        <button onClick={handleGenerate} disabled={generating || !selectedPeriod} className="w-full p-3 rounded-xl border-0 bg-brand-primary text-white cursor-pointer font-bold text-[0.95rem] mt-2">{generating ? 'Generating...' : 'Generate summary'}</button>
      </div>

      {result && (
        <div className="p-[18px] rounded-2xl bg-white" style={{ border: '1px solid #2dd4a8' }}>
          <h3 className="mt-0 mb-2 text-brand-dark">
            {result.periodLabel}
          </h3>
          <div className="text-[0.85rem] text-body mb-3">
            {result.tripCount} completed trip{result.tripCount !== 1 ? 's' : ''}
          </div>

          {result.tripCount > 0 && (
            <>
              <div className="grid gap-1 text-[0.85rem] mb-3">
                <div className="flex justify-between">
                  <span className="text-body">Total fares (excl GST + levy):</span>
                  <span className="font-semibold">${result.summary.totalFareExclGstAndLevy.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-body">GST collected:</span>
                  <span className="font-semibold">${result.summary.totalGstCollected.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-body">NSW levy (passed to govt):</span>
                  <span className="font-semibold">${result.summary.totalLevy.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-body">Reservo commission:</span>
                  <span className="font-semibold" style={{ color: '#dc2626' }}>-${result.summary.totalCommission.toFixed(2)}</span>
                </div>
                <div className="flex justify-between pt-2 mt-1" style={{ borderTop: '2px solid #e2e8f0' }}>
                  <span className="font-bold text-brand-dark">Net earnings to you:</span>
                  <span className="font-extrabold text-[1.1rem]" style={{ color: '#059669' }}>${result.summary.totalNetEarnings.toFixed(2)}</span>
                </div>
              </div>
            </>
          )}

          {result.tripCount > 0 && (
            <div className="flex gap-2 mt-3">
              <button onClick={() => downloadFromSignedUrl(result.pdfUrl, `reservo-tax-summary-${result.period}.pdf`)} className="flex-1 p-2.5 rounded-[10px] border-0 bg-brand-primary text-white cursor-pointer font-semibold">📄 Download PDF</button>
              <button onClick={() => downloadFromSignedUrl(result.csvUrl, `reservo-tax-summary-${result.period}.csv`)} className="flex-1 p-2.5 rounded-[10px] border-[1.5px] border-brand-primary bg-white text-brand-primary cursor-pointer font-semibold">📊 Download CSV</button>
            </div>
          )}

          <div className="p-2.5 rounded-lg mt-3 text-[0.78rem]" style={{ background: '#fef3c7', border: '1px solid #fde68a', color: '#92400e' }}>
            <strong>For BAS:</strong> If you're GST-registered, the GST shown above is what you collected from riders. You're responsible for remitting it to the ATO. Reservo's commission is a deductible business expense. Verify all figures against your own records.
          </div>
        </div>
      )}
      <BackButtonBottom to="/driver" label="Dashboard" />
    </div>
  );
}